// src/pages/BlogPage.tsx

import { Link } from 'react-router-dom';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
  slug: string;
  featuredImage?: string;
}

const blogPosts: BlogPost[] = [
  {
    id: '1',
    title: '10 Instagram Conversation Automation Strategies That Will Boost Your Engagement by 300%',
    excerpt: 'Discover proven strategies to automate your Instagram conversations and skyrocket your engagement rates. Learn how AI-powered responses can transform your customer interactions.',
    author: 'Chatutu Team',
    date: 'January 10, 2024',
    readTime: '8 min read',
    tags: ['Instagram Marketing', 'Automation', 'Engagement', 'AI'],
    image: '/blog/instagram-automation.jpg',
    slug: '10-instagram-conversation-automation-strategies',
    featuredImage: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800&h=400&fit=crop&crop=center'
  },
  {
    id: '2',
    title: 'Why Instagram Conversation Automation is Essential for Business Growth in 2024',
    excerpt: 'Explore how Instagram conversation automation tools are revolutionizing business communication. Learn about the latest AI technologies, automation best practices, and real-world case studies from successful Instagram marketers.',
    author: 'Chatutu Team',
    date: 'January 15, 2024',
    readTime: '12 min read',
    tags: ['Business Growth', 'Instagram Marketing', 'AI Technology', 'Automation'],
    image: '/blog/business-growth.jpg',
    slug: 'why-instagram-conversation-automation-essential-2024',
    featuredImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=400&fit=crop&crop=center'
  }
];

export function BlogPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Chatutu Blog
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Insights, strategies, and best practices for Instagram marketing automation
            </p>
          </div>
        </div>
      </div>

      {/* Blog Posts */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {blogPosts.map((post) => (
            <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-brand-100 transform hover:-translate-y-1">
              <div className="aspect-video bg-brand-200 relative overflow-hidden">
                {post.featuredImage ? (
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <span className="text-white text-5xl">📱</span>
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <div className="flex flex-wrap gap-2">
                    {post.tags.slice(0, 2).map((tag, index) => (
                      <span key={index} className="px-3 py-1 bg-white bg-opacity-90 text-primary-800 text-xs rounded-full font-medium backdrop-blur-sm">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <h2 className="text-xl font-bold text-brand-900 mb-3 line-clamp-2 leading-tight">
                  <Link to={`/blog/${post.slug}`} className="hover:text-primary-600 transition-colors">
                    {post.title}
                  </Link>
                </h2>
                <p className="text-brand-600 mb-4 line-clamp-3 leading-relaxed">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between text-sm text-brand-500">
                  <div className="flex items-center space-x-4">
                    <span className="font-medium">{post.author}</span>
                    <span>•</span>
                    <span>{post.readTime}</span>
                  </div>
                </div>
                <div className="mt-2 text-sm text-brand-400">
                  {post.date}
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Stay Updated with Instagram Marketing Tips
          </h2>
          <p className="text-xl mb-6 opacity-90">
            Get weekly insights on automation, engagement strategies, and Instagram growth hacks
          </p>
          <div className="max-w-md mx-auto flex gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 placeholder-gray-500"
            />
            <button className="px-6 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
              Subscribe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}